import axios, { AxiosError } from 'axios';
import autobind from 'autobind-decorator';
import { config } from '../../config';
import { RootStore } from '../store';

interface APIHandlers {
  auth_token_error: () => void;
}

@autobind
export class APIStore {
  readonly handlers: APIHandlers;
  store: RootStore;

  constructor(store: RootStore, handlers: APIHandlers) {
    this.store = store;
    this.handlers = handlers;
  }

  // API REQUEST HELPERS
  async request(args: T.API.RequestArgs): Promise<any> {
    const { path, data, params, headers } = args;
    try {
      const res = await axios({
        baseURL: config.urls.api + '/',
        url: path,
        method: 'post',
        headers: headers || {},
        params: params || {},
        data: data || {},
      });
      return res.data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response && e.response.data) {
        // === "error-auth-token"
        if (e.response.data.message === 'Invalid Authentication') {
          this.handlers.auth_token_error();
          return { outcome: 1, message: '' };
        } else {
          throw e.response.data;
        }
      } else {
        throw e;
      }
    }
  }
  async request_public(args: T.API.RequestPublicPrivateArgs): Promise<any> {
    const { path, data, params } = args;
    const headers = {
      'Authorization-Public': config.services.api.public_key,
    };
    return this.request({ path, data, params, headers });
  }
  async request_auth(args: T.API.RequestPublicPrivateArgs): Promise<any> {
    const { path, data, params } = args;
    const token = this._get_auth_token();
    if (config.isTest && !token) return;
    const headers = { 'Authorization-Dashboard': `${token}` };
    return this.request({ path, data, params, headers });
  }

  // RESELLER
  async reseller_find_public(data: T.API.ResellerFindPublicRequest): Promise<T.API.ResellerFindPublicResponse> {
    return this.request_public({
      path: '/resellers/find/dashboard/public',
      data,
    });
  }

  // USER
  async user_register_commence(data: T.API.UserRegisterCommenceRequest): Promise<T.API.UserRegisterCommenceResponse> {
    return this.request_public({
      path: '/users/register/commence',
      data,
    });
  }
  async user_register_complete(data: T.API.UserRegisterCompleteRequest): Promise<T.API.UserRegisterCompleteResponse> {
    return this.request_public({
      path: '/users/register/complete',
      data,
    });
  }
  async user_login(data: T.API.UserLoginRequest): Promise<T.API.UserLoginResponse> {
    return this.request_public({
      path: '/users/login',
      data,
    });
  }
  async user_password_reset_init(
    data: T.API.UserPasswordResetInitRequest
  ): Promise<T.API.UserPasswordResetInitResponse> {
    return this.request_public({
      path: '/users/password-reset/init',
      data,
    });
  }
  async user_password_reset_change(
    data: T.API.UserPasswordResetChangeRequest
  ): Promise<T.API.UserPasswordResetChangeResponse> {
    return this.request_public({
      path: '/users/password-reset/change',
      data,
    });
  }
  async user_token_refresh(): Promise<T.API.UserTokenRefreshResponse> {
    return this.request_auth({
      path: '/users/token/refresh',
    });
  }
  async user_profile_update(data: T.API.UserUpdateProfileRequest): Promise<T.API.UserUpdateProfileResponse> {
    return this.request_auth({
      path: '/users/update/profile',
      data,
    });
  }

  // RESTAURANT
  async restaurant_find(id: string): Promise<T.Schema.Restaurant.RestaurantSchema> {
    return this.request_auth({
      path: `/restaurants/find/`,
      data: { id: id },
    }).then((restaurant: any) => {
      // Seeds the lock: without it the session's first save carries no expectation.
      this.note_menu_version(id, restaurant?.menu_version);
      return restaurant;
    });
  }
  async restaurant_create(data: T.API.RestaurantCreateRequest): Promise<T.API.RestaurantCreateResponse> {
    return this.request_auth({
      path: '/restaurants/create',
      data,
    });
  }
  /**
   * Every menu save in the original dashboard funnels through here, which is
   * why the optimistic lock is plumbed in one place rather than at the 19 call
   * sites.
   *
   * Two halves, and the second is not optional. Sending the expectation is what
   * turns a silent overwrite into a refusal. Recording what the server returns
   * is what stops the session's *second* save being refused as stale.
   *
   * The server enforces the lock only for updates that touch menu data.
   */
  async restaurant_update(data: T.API.RestaurantUpdateRequest): Promise<T.API.RestaurantUpdateResponse> {
    const expected = data.expected_menu_version ?? this.menu_version_for(data._id);

    const res: T.API.RestaurantUpdateResponse = await this.request_auth({
      path: '/restaurants/update',
      data: { ...data, expected_menu_version: expected },
    });

    const error = res as T.API.MenuConflictResponse;
    if (error?.outcome === 1 && error.conflict === 'menu_version') {
      // Returns before noting, and the order is the whole point. A refusal
      // carries the server's version too, so recording it here would mean
      // dismissing the dialog was enough to overwrite: the next save would
      // carry a matching expectation and sail through. The dialog's own Keep
      // mine passes that version explicitly, so overwriting is an answer
      // rather than a side effect of closing a modal.
      this.store.menu_conflict.raise(data._id, error);
      return res;
    }

    this.note_menu_version(data._id, (res as T.API.MenuWriteResponse)?.menu_version);

    return res;
  }

  /**
   * Last known menu_version per restaurant, held here rather than on the
   * restaurant record because both APIs advance it — the CW API in the response
   * body, menu-service in a header — and they must not disagree.
   */
  private menu_versions_held = new Map<string, number>();

  note_menu_version(restaurant_id: string, version?: number) {
    if (!restaurant_id || typeof version !== 'number' || version <= 0) return;

    // Monotonic: an out-of-order response must not walk it backwards.
    const held = this.menu_versions_held.get(restaurant_id) ?? 0;
    if (version > held) this.menu_versions_held.set(restaurant_id, version);
  }

  menu_version_for(restaurant_id: string): number | undefined {
    return this.menu_versions_held.get(restaurant_id);
  }
  /**
   * The assistant never writes to the database. It returns a proposal that the
   * caller applies to the open settings form, so the operator's existing Save is
   * still the only thing that reaches a live storefront.
   */
  async ai_website_chat(data: T.API.AIWebsiteChatRequest): Promise<T.API.AIWebsiteChatResponse> {
    return this.request_auth({
      path: '/ai/website/chat',
      data,
    });
  }
  async restaurant_update_operation(
    data: T.API.RestaurantUpdateOperationsRequest
  ): Promise<T.API.RestaurantUpdateOperationsResponse> {
    return this.request_auth({
      path: '/restaurants/update/operations',
      data,
    });
  }
  async restaurant_update_subscription(
    data: T.API.RestaurantUpdateSubscriptionRequest
  ): Promise<T.API.RestaurantUpdateSubscriptionResponse> {
    return this.request_auth({
      path: '/restaurants/update/subscription',
      data,
    });
  }
  async restaurant_delete(data: T.API.RestaurantDeleteRequest): Promise<T.API.RestaurantDeleteResponse> {
    return this.request_auth({
      path: '/restaurants/delete',
      data,
    });
  }
  async restaurant_menu_delete(data: T.API.RestaurantMenuDeleteRequest): Promise<T.API.RestaurantMenuDeleteResponse> {
    return this.request_auth({
      path: '/restaurants/menu/delete',
      data,
    });
  }

  // PROXY
  async proxy_subdomain_check(
    data: T.API.ProxySubdomainCheckUpdateRequest
  ): Promise<T.API.ProxySubdomainCheckUpdateResponse> {
    return this.request_auth({
      path: '/proxy/subdomain/check',
      data,
    });
  }
  async proxy_subdomain_update(
    data: T.API.ProxySubdomainCheckUpdateRequest
  ): Promise<T.API.ProxySubdomainCheckUpdateResponse> {
    return this.request_auth({
      path: '/proxy/subdomain/update',
      data,
    });
  }
  async proxy_domain_check(data: T.API.ProxyDomainCheckUpdateRequest): Promise<T.API.ProxyDomainCheckUpdateResponse> {
    return this.request_auth({
      path: '/proxy/domain/check',
      data,
    });
  }
  async proxy_domain_update(data: T.API.ProxyDomainCheckUpdateRequest): Promise<T.API.ProxyDomainCheckUpdateResponse> {
    return this.request_auth({
      path: '/proxy/domain/update',
      data,
    });
  }
  async proxy_domain_cert_check(data: T.API.ProxyCertCheckRequest): Promise<T.API.ProxyCertCheckResponse> {
    return this.request_auth({
      path: '/proxy/cert/check',
      data,
    });
  }
  async proxy_domain_cert_issue(data: T.API.ProxyCertIssueRequest): Promise<T.API.ProxyCertIssueResponse> {
    return this.request_auth({
      path: '/proxy/cert/issue',
      data,
    });
  }

  // STAFF
  async staff_find(data: T.API.StaffFindRequest): Promise<T.API.StaffFindResponse> {
    return this.request_auth({
      path: '/dashboard/staff/find',
      data,
    });
  }
  async staff_create(data: T.API.StaffCreateRequest): Promise<T.API.StaffCreateResponse> {
    return this.request_auth({
      path: '/dashboard/staff/create',
      data,
    });
  }
  async staff_update(data: T.API.StaffUpdateRequest): Promise<T.API.StaffUpdateResponse> {
    return this.request_auth({
      path: '/dashboard/staff/update',
      data,
    });
  }
  async staff_delete(data: T.API.StaffDeleteRequest): Promise<T.API.StaffDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/staff/delete',
      data,
    });
  }

  // API ACCESS
  async api_find(data: T.API.APIFindRequest): Promise<T.API.APIFindResponse> {
    return this.request_auth({
      path: '/dashboard/api/find',
      data,
    });
  }
  async api_create(data: T.API.APICreateRequest): Promise<T.API.APICreateResponse> {
    return this.request_auth({
      path: '/dashboard/api/create',
      data,
    });
  }
  async api_update(data: T.API.APIUpdateRequest): Promise<T.API.APIUpdateResponse> {
    return this.request_auth({
      path: '/dashboard/api/update',
      data,
    });
  }
  async api_delete(data: T.API.APIDeleteRequest): Promise<T.API.APIDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/api/delete',
      data,
    });
  }
  async mcp_usage(data: T.API.McpUsageRequest): Promise<T.API.McpUsageResponse> {
    return this.request_auth({
      path: '/dashboard/mcp/usage',
      data,
    });
  }

  async mcp_login_code(token: string): Promise<{ outcome: number; code?: string; message?: string }> {
    return this.request({
      path: '/dashboard/mcp/login-code',
      data: {},
      headers: { 'Authorization-Dashboard': `${token}` },
    });
  }

  async mcp_connections(): Promise<{ outcome: number; items?: T.API.McpConnection[]; message?: string }> {
    return this.request_auth({ path: '/dashboard/mcp/connections', data: {} });
  }

  async mcp_connection_revoke(data: { id: string }): Promise<{ outcome: number; message?: string }> {
    return this.request_auth({ path: '/dashboard/mcp/connections/revoke', data });
  }

  async mcp_connection_restaurants(
    data: T.API.McpConnectionRestaurantsRequest
  ): Promise<T.API.McpConnectionRestaurantsResponse> {
    return this.request_auth({ path: '/dashboard/mcp/connections/restaurants', data });
  }

  async mcp_usage_series(data: T.API.McpUsageSeriesRequest): Promise<T.API.McpUsageSeriesResponse> {
    return this.request_auth({ path: '/dashboard/mcp/usage/series', data });
  }

  async mcp_limits(data: T.API.McpLimitsRequest): Promise<T.API.McpLimitsResponse> {
    return this.request_auth({ path: '/dashboard/mcp/limits', data });
  }

  async mcp_limits_update(data: T.API.McpLimitsUpdateRequest): Promise<T.API.McpLimitsUpdateResponse> {
    return this.request_auth({ path: '/dashboard/mcp/limits/update', data });
  }

  // DEVICES
  async devices_request(data: T.API.DevicesRequestRequest): Promise<T.API.DevicesRequestResponse> {
    return this.request_auth({
      path: '/dashboard/devices/request',
      data,
    });
  }
  async devices_pairing_code(data: T.API.DevicesPairingCodeRequest): Promise<T.API.DevicesPairingCodeResponse> {
    return this.request_auth({
      path: '/dashboard/devices/pairing-code',
      data,
    });
  }
  async devices_find(data: T.API.DevicesFindRequest): Promise<T.API.DevicesFindResponse> {
    return this.request_auth({
      path: '/dashboard/devices/find',
      data,
    });
  }
  async devices_delete(data: T.API.DevicesDeleteRequest): Promise<T.API.DevicesDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/devices/delete',
      data,
    });
  }
  async devices_nickname(data: T.API.DevicesNicknameRequest): Promise<T.API.DevicesNicknameResponse> {
    return this.request_auth({
      path: '/dashboard/devices/nickname',
      data,
    });
  }

  // WEBSITE
  async website_find(data: T.API.WebsiteFindRequest): Promise<T.API.WebsiteFindResponse> {
    return this.request_auth({
      path: '/website/dashboard/find',
      data,
    });
  }
  async website_create(data: T.API.DashboardWebsiteCreateRequest): Promise<T.API.DashboardWebsiteCreateResponse> {
    return this.request_auth({
      path: '/website/dashboard/create',
      data,
    });
  }
  async website_update(data: T.API.DashboardWebsiteUpdateRequest): Promise<T.API.DashboardWebsiteUpdateResponse> {
    return this.request_auth({
      path: '/website/dashboard/update',
      data,
    });
  }

  //organisation
  async organisation_update(data: T.API.DashboardWebsiteUpdateRequest): Promise<T.API.DashboardWebsiteUpdateResponse> {
    return this.request_auth({
      path: '/organisation/dashboard/update',
      data,
    });
  }

  // DASHBOARD
  async dashboard_data(): Promise<T.API.DashboardDataResponse> {
    return this.request_auth({
      path: '/dashboard/data',
    });
  }
  async restaurants(): Promise<T.API.DashboardRestaurantsResponse> {
    return this.request_auth({
      path: '/dashboard/restaurants',
    });
  }
  async restaurant(data: T.API.DashboardRestaurantFindRequest): Promise<T.API.DashboardRestaurantFindResponse> {
    return this.request_auth({
      path: '/dashboard/restaurant',
      data,
    });
  }
  async billing_session(data: T.API.DashboardBillingSessionRequest): Promise<T.API.DashboardBillingSessionResponse> {
    return this.request_auth({
      path: '/dashboard/billing/session',
      data,
    });
  }

  // DASHBOARD - RESTAURANT SPECIFIC
  async reports_basic(data: T.API.DashboardReportsBasicRequest): Promise<T.API.DashboardReportsBasicResponse> {
    return this.request_auth({
      path: '/dashboard/reports/basic',
      data,
    });
  }

  async send_overview_report_email(
    data: T.API.DashboardReportsSendingEmailRequest
  ): Promise<T.API.DashboardReportsSendingEmailResponse> {
    return this.request_auth({
      path: '/dashboard/reports/sendEmail',
      data,
    });
  }

  async menu_stock_find(data: T.API.DashboardMenuStockFindReq): Promise<T.API.DashboardMenuStockFindRes> {
    return this.request_auth({
      path: '/dashboard/menu/stock/find',
      data,
    });
  }
  // Kounta Requests
  async get_kounta_sites(_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/sites',
      data: { _id: _id },
    });
  }
  async get_kounta_registers(_id: any, site_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/registers',
      data: { _id: _id, site_id: site_id },
    });
  }
  async get_kounta_payments(_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/payment_methods',
      data: { _id: _id },
    });
  }
  async get_kounta_delivery_products(_id: any, site_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/delivery_products',
      data: { _id: _id, site_id: site_id },
    });
  }
  async generate_menu(_id: any, menu_id: string): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/generate_menu',
      data: { _id: _id, menu_id: menu_id },
    });
  }
  async generate_menuv2(_id: any, menu_id: string): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/generate_menu_v2',
      data: { _id: _id, menu_id: menu_id },
    });
  }
  async generate_menu_abacus(_id: any, menu_id: string): Promise<any> {
    return this.request_auth({
      path: '/dashboard/abacus/generate_menu',
      data: { _id: _id, menu_id: menu_id },
    });
  }
  // PushPOS sync, straight to menu-service like the other menu calls, so both answer with
  // menu-service's own body rather than { outcome, data }.
  async sync_menu_wpos(restaurant_id: string): Promise<any> {
    return this.request_auth_menu_api({
      path: `api/${restaurant_id}/pos/sync`,
      method: 'POST',
    });
  }
  async sync_status_wpos(restaurant_id: string): Promise<any> {
    return this.request_auth_menu_api({
      path: `api/${restaurant_id}/pos/status`,
      method: 'GET',
    });
  }
  async check_status(_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/kounta/check_status',
      data: { _id: _id },
    });
  }
  async check_status_abacus(_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/abacus/check_status',
      data: { _id: _id },
    });
  }
  async check_status_wpos(_id: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/wpos/check_status',
      data: { _id: _id },
    });
  }
  async menu_stock_update(data: T.API.DashboardMenuStockUpdateReq): Promise<T.API.DashboardMenuStockUpdateRes> {
    return this.request_auth({
      path: '/dashboard/menu/stock/update',
      data,
    });
  }

  async orders_board_find(
    data: T.API.DashboardOrdersBoardFindRequest
  ): Promise<T.API.DashboardOrdersBoardFindResponse> {
    return this.request_auth({
      path: '/dashboard/orders/board',
      data: data,
    });
  }
  async orders_find(data: T.API.DashboardOrdersFindRequest): Promise<T.API.DashboardOrdersFindResponse> {
    return this.request_auth({
      path: '/dashboard/orders',
      data: data,
    });
  }
  async orders_bump_find(data: T.API.DashboardOrdersFindRequest): Promise<T.API.DashboardOrdersFindResponse> {
    return this.request_auth({
      path: '/dashboard/orders/bump',
      data: data,
    });
  }

  async orders_bulk_update_status(
    data: T.API.DashboardOrdersUpdateStatusRequest
  ): Promise<T.API.DashboardOrdersUpdateStatusResponse> {
    return this.request_auth({
      path: '/dashboard/orders/update/status',
      data: data,
    });
  }

  async orders_bulk_delete(data: T.API.DashboardOrdersDeleteRequest): Promise<T.API.DashboardOrderDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/orders/delete',
      data: data,
    });
  }

  async menu_dish_update_operations(
    data: T.API.DashboardOrderDishUpdateOperationsRequest
  ): Promise<T.API.CommonResponse> {
    return this.request_auth({
      path: '/restaurants/update/menu/operations',
      data: data,
    });
  }
  async order_find(data: T.API.DashboardOrderFindRequest): Promise<T.API.DashboardOrderFindResponse> {
    return this.request_auth({
      path: '/dashboard/order',
      data: data,
    });
  }
  async order_update_status(
    data: T.API.DashboardOrderUpdateStatusRequest
  ): Promise<T.API.DashboardOrderUpdateStatusResponse> {
    return this.request_auth({
      path: '/dashboard/order/update/status',
      data: data,
    });
  }
  async order_dish_update_status(
    data: T.API.DashboardOrderDishUpdateStatusRequest
  ): Promise<T.API.DashboardOrderUpdateStatusResponse> {
    return this.request_auth({
      path: '/dashboard/order_dish/update/status',
      data: data,
    });
  }
  async order_update_ready_time(
    data: T.API.DashboardOrderUpdateReadyTimeRequest
  ): Promise<T.API.DashboardOrderUpdateReadyTimeResponse> {
    return this.request_auth({
      path: '/dashboard/order/update/ready-time',
      data: data,
    });
  }
  async order_book_tookan(
    data: T.API.DashboardOrderTookanBookRequest
  ): Promise<T.API.DashboardOrderTookanBookResponse> {
    return this.request_auth({
      path: '/dashboard/order/tookan/book',
      data: data,
    });
  }
  async order_delete(data: T.API.DashboardOrderDeleteRequest): Promise<T.API.DashboardOrderDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/order/delete',
      data: data,
    });
  }
  async order_stripe_refund(
    data: T.API.DashboardOrderStripeRefundRequest
  ): Promise<T.API.DashboardOrderStripeRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/stripe/refund',
      data: data,
    });
  }
  async order_authorize_refund(
    data: T.API.DashboardOrderAuthorizeRefundRequest
  ): Promise<T.API.DashboardOrderStripeRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/authorized/refund',
      data: data,
    });
  }
  async order_nuvei_refund(data: any) {
    return this.request_auth({
      path: '/dashboard/order/nuvei/refund',
      data: data,
    });
  }
  async order_stripe_connect_refund(
    data: T.API.DashboardOrderStripeConnectRefundRequest
  ): Promise<T.API.DashboardOrderStripeRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/stripe-connect/refund',
      data: data,
    });
  }
  async order_cardconnect_refund(
    data: T.API.DashboardOrderCardConnectRefundRequest
  ): Promise<T.API.DashboardOrderCardConnectRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/cardconnect/refund',
      data: data,
    });
  }
  async order_verify_checkout_payment(
    data: T.API.DashboardOrderCheckoutPaymentVerificationRequest
  ): Promise<T.API.DashboardOrderCheckoutPaymentVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/checkout/verify-payment',
      data,
    });
  }

  async orderVerifySwishPayment(
    data: T.API.DashboardOrderSwishPaymentVerificationRequest
  ): Promise<T.API.DashboardOrderSwishVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/swish/verify-payment',
      data,
    });
  }

  async orderVerifySwishRefund(
    data: T.API.DashboardOrderSwishRefundVerificationRequest
  ): Promise<T.API.DashboardOrderSwishRefundVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/swish/verify-refund',
      data,
    });
  }

  async orderCreateSwishRefund(data: T.API.DashboardOrderRefundRequest): Promise<T.API.DashboardOrderRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/swish/refund',
      data,
    });
  }

  async orderCreatePayMongoRefund(
    data: T.API.DashboardOrderPayMongoRefundRequest
  ): Promise<T.API.DashboardOrderRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/paymongo/refund',
      data,
    });
  }

  async orderVerifyPayMongoRefund(
    data: T.API.DashboardOrderPayMongoRefundVerificationRequest
  ): Promise<T.API.DashboardOrderPayMongoRefundVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/paymongo/verify-refund',
      data,
    });
  }

  async orderVerifyPayMongoPayment(
    data: T.API.DashboardOrderPayMongoPaymentVerificationRequest
  ): Promise<T.API.DashboardOrderPayMongoPaymentVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/paymongo/verify-payment',
      data,
    });
  }

  async orderCreateFiservRefund(data: T.API.DashboardOrderRefundRequest): Promise<T.API.DashboardOrderRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/fiserv/refund',
      data,
    });
  }

  async orderCreateBamboraNaRefund(
    data: T.API.DashboardOrderRefundRequest
  ): Promise<T.API.DashboardOrderRefundResponse> {
    return this.request_auth({
      path: '/dashboard/order/bambora_na/refund',
      data,
    });
  }

  async orderVerifyFiservOrder(
    data: T.API.DashboardOrderVerificationRequest
  ): Promise<T.API.DashboardOrderVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/fiserv/verify-order',
      data,
    });
  }

  async orderVerifyVivaWalletTransaction(
    data: T.API.DashboardOrderVerificationRequest
  ): Promise<T.API.DashboardOrderVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/viva-wallet/verify-transaction',
      data,
    });
  }

  async orderVerifyPaywayPayment(
    data: T.API.DashboardOrderPaywayVerificationRequest
  ): Promise<T.API.DashboardOrderPaywayVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/payway/verify-payment',
      data,
    });
  }

  async orderVerifyGkashPayment(
    data: T.API.DashboardOrderGkashPaymentVerificationRequest
  ): Promise<T.API.DashboardOrderGkashPaymentVerificationResponse> {
    return this.request_auth({
      path: '/dashboard/order/gkash/verify-payment',
      data,
    });
  }

  async restaurantRegisterPayMongoWebhook(data: { restaurantId: string }): Promise<T.API.CommonResponse> {
    return this.request_auth({
      path: 'dashboard/restaurant/paymongo/register-webhook',
      data,
    });
  }

  async bookings_find(data: T.API.DashboardBookingsFindRequest): Promise<T.API.DashboardBookingsFindResponse> {
    return this.request_auth({
      path: '/dashboard/bookings',
      data,
    });
  }
  async booking_find(data: T.API.DashboardBookingFindRequest): Promise<T.API.DashboardBookingFindResponse> {
    return this.request_auth({
      path: '/dashboard/booking',
      data,
    });
  }
  async booking_create(data: T.API.DashboardBookingCreateRequest): Promise<T.API.DashboardBookingCreateResponse> {
    return this.request_auth({
      path: '/dashboard/booking/create',
      data,
    });
  }
  async booking_update_status(
    data: T.API.DashboardBookingUpdateStatusRequest
  ): Promise<T.API.DashboardBookingUpdateStatusResponse> {
    return this.request_auth({
      path: '/dashboard/booking/update/status',
      data,
    });
  }
  async booking_update_fields(
    data: T.API.DashboardBookingUpdateFieldsRequest
  ): Promise<T.API.DashboardBookingUpdateFieldsResponse> {
    return this.request_auth({
      path: '/dashboard/booking/update/fields',
      data,
    });
  }
  async booking_delete(data: T.API.DashboardBookingDeleteRequest): Promise<T.API.DashboardBookingDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/booking/delete',
      data,
    });
  }

  async print_receipt(data: T.API.DashboardPrintRequest): Promise<T.API.DashboardPrintResponse> {
    return this.request_auth({
      path: '/printing/dashboard/print',
      data,
    });
  }

  async print_report(data: T.API.DashboardReportsBasicRequest): Promise<T.API.DashboardPrintResponse> {
    return this.request_auth({
      path: '/printing/dashboard/report/print',
      data,
    });
  }

  async customers_find(data: T.API.DashboardCustomersFindRequest): Promise<T.API.DashboardCustomersFindResponse> {
    return this.request_auth({
      path: '/dashboard/customers',
      data,
    });
  }
  async customer_find(data: T.API.DashboardCustomerFindRequest): Promise<T.API.DashboardCustomerFindResponse> {
    return this.request_auth({
      path: '/dashboard/customer',
      data,
    });
  }
  async customer_update_dashboard(
    data: T.API.DashboardCustomerUpdateDashboardRequest
  ): Promise<T.API.DashboardCustomerUpdateDashboardResponse> {
    return this.request_auth({
      path: '/dashboard/customer/update/dashboard',
      data,
    });
  }
  async customer_create(data: T.API.DashboardCustomerCreateRequest): Promise<T.API.DashboardCustomerCreateResponse> {
    return this.request_auth({
      path: '/dashboard/customer/create',
      data,
    });
  }
  async customer_update_email(
    data: T.API.DashboardCustomerUpdateEmailRequest
  ): Promise<T.API.DashboardCustomerUpdateEmailResponse> {
    return this.request_auth({
      path: '/dashboard/customer/update/email',
      data,
    });
  }
  async customer_update_password(
    data: T.API.DashboardCustomerUpdatePasswordRequest
  ): Promise<T.API.DashboardCustomerUpdatePasswordResponse> {
    return this.request_auth({
      path: '/dashboard/customer/update/password',
      data,
    });
  }
  async customer_age_verification_update(
    data: T.API.DashboardCustomerAgeVerificationUpdateRequest
  ): Promise<T.API.DashboardCustomerAgeVerificationUpdateResponse> {
    return this.request_auth({
      path: '/dashboard/customer/age-verification/update',
      data,
    });
  }
  async customer_delete(data: T.API.DashboardCustomerDeleteRequest): Promise<T.API.DashboardCustomerDeleteResponse> {
    return this.request_auth({
      path: '/dashboard/customer/delete',
      data,
    });
  }
  async dashboard_restaurant_domain_verify(
    data: T.API.DashboardRestaurantDomainVerificationRequest
  ): Promise<T.API.DashboardRestaurantDomainVerificationResponse> {
    return this.request_auth({
      path: '/v2/stripe-payment/restaurant/domain/verify',
      data,
    });
  }
  async dashboard_restaurant_payment_prefill_stripe(
    data: T.API.DashboardPaymentPrefillStripeDigitalPayRequest
  ): Promise<T.API.DashboardPaymentPrefillStripeResponse> {
    return this.request_auth({
      path: '/v2/stripe-payment/restaurant/payment/prefill/stripe',
      data,
    });
  }
  async dashboard_restaurant_payment_prefill_stripedigitalpay(
    data: T.API.DashboardPaymentPrefillStripeDigitalPayRequest
  ): Promise<T.API.DashboardPaymentPrefillStripeDigitalPayResponse> {
    return this.request_auth({
      path: '/v2/stripe-payment/restaurant/payment/prefill/stripe_digital_wallet',
      data,
    });
  }

  async printer_status(data: T.API.DashboardPrinterStatusReq): Promise<T.API.DashboardPrinterStatusRes> {
    return this.request_auth({
      path: '/dashboard/printers/status',
      data,
    });
  }

  async getStripeConnect(data: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/connect/get-transactions',
      data,
    });
  }

  async native_loyalty_connect(data: T.API.NativeLoyaltyConnectRequest): Promise<T.API.NativeLoyaltyConnectResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/connect',
      data,
    });
  }

  async native_loyalty_billing_status(
    data: T.API.NativeLoyaltyBillingStatusRequest
  ): Promise<T.API.NativeLoyaltyBillingStatusResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/status',
      data,
    });
  }

  async native_loyalty_billing_checkout(
    data: T.API.NativeLoyaltyBillingCheckoutRequest
  ): Promise<T.API.NativeLoyaltyBillingCheckoutResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/checkout',
      data,
    });
  }

  async native_loyalty_billing_confirm(
    data: T.API.NativeLoyaltyBillingConfirmRequest
  ): Promise<T.API.NativeLoyaltyBillingConfirmResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/confirm',
      data,
    });
  }

  async native_loyalty_billing_portal(
    data: T.API.NativeLoyaltyBillingPortalRequest
  ): Promise<T.API.NativeLoyaltyBillingPortalResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/portal',
      data,
    });
  }

  async native_loyalty_org_billing_status(): Promise<T.API.NativeLoyaltyOrgBillingStatusResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/organisation/status',
      data: {},
    });
  }

  async native_loyalty_org_billing_checkout(
    data: T.API.NativeLoyaltyOrgBillingCheckoutRequest
  ): Promise<T.API.NativeLoyaltyOrgBillingCheckoutResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/organisation/checkout',
      data,
    });
  }

  async native_loyalty_org_billing_confirm(
    data: T.API.NativeLoyaltyOrgBillingConfirmRequest
  ): Promise<T.API.NativeLoyaltyOrgBillingConfirmResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/organisation/confirm',
      data,
    });
  }

  async native_loyalty_org_billing_portal(
    data: T.API.NativeLoyaltyOrgBillingPortalRequest
  ): Promise<T.API.NativeLoyaltyOrgBillingPortalResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/billing/organisation/portal',
      data,
    });
  }

  async native_loyalty_handoff_token(
    data: T.API.NativeLoyaltyDashboardRestaurantRequest
  ): Promise<T.API.NativeLoyaltyHandoffTokenResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/handoff-token',
      data,
    });
  }

  async native_loyalty_platform_link(
    data: T.API.NativeLoyaltyDashboardRestaurantRequest
  ): Promise<T.API.NativeLoyaltyPlatformLinkResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/platform-link',
      data,
    });
  }

  async native_loyalty_sync_logo(data: T.API.NativeLoyaltyLogoSyncRequest): Promise<T.API.NativeLoyaltyLogoSyncResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/logo/sync',
      data,
    });
  }

  async native_loyalty_platform_link_update(
    data: T.API.NativeLoyaltyPlatformLinkUpdateRequest
  ): Promise<T.API.NativeLoyaltyPlatformLinkResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/platform-link/update',
      data,
    });
  }

  async native_loyalty_overview(
    data: T.API.NativeLoyaltyOverviewRequest
  ): Promise<T.API.NativeLoyaltyOverviewResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/overview',
      data,
    });
  }

  async native_loyalty_activity(
    data: T.API.NativeLoyaltyActivityRequest
  ): Promise<T.API.NativeLoyaltyActivityResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/activity',
      data,
    });
  }

  async native_loyalty_members(
    data: T.API.NativeLoyaltyMembersRequest
  ): Promise<T.API.NativeLoyaltyMembersResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/members',
      data,
    });
  }

  async native_loyalty_member_detail(
    data: T.API.NativeLoyaltyMemberDetailRequest
  ): Promise<T.API.NativeLoyaltyMemberDetailResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/members/detail',
      data,
    });
  }

  async native_loyalty_member_active(
    data: T.API.NativeLoyaltyMemberActiveRequest
  ): Promise<T.API.NativeLoyaltyMemberActiveResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/members/active',
      data,
    });
  }

  async native_loyalty_loyalty_program(
    data: T.API.NativeLoyaltyDashboardRestaurantRequest
  ): Promise<T.API.NativeLoyaltyProgramResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/loyalty-program',
      data,
    });
  }

  async native_loyalty_loyalty_program_update(
    data: T.API.NativeLoyaltyProgramUpdateRequest
  ): Promise<T.API.NativeLoyaltyProgramResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/loyalty-program/update',
      data,
    });
  }

  async native_loyalty_gift_card_program(
    data: T.API.NativeLoyaltyDashboardRestaurantRequest
  ): Promise<T.API.NativeLoyaltyProgramResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-card-program',
      data,
    });
  }

  async native_loyalty_gift_card_program_update(
    data: T.API.NativeLoyaltyProgramUpdateRequest
  ): Promise<T.API.NativeLoyaltyProgramResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-card-program/update',
      data,
    });
  }

  async native_loyalty_points_adjust(
    data: T.API.NativeLoyaltyPointsAdjustRequest
  ): Promise<T.API.NativeLoyaltyPointsAdjustResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/members/points-adjust',
      data,
    });
  }

  async native_loyalty_gift_card_issue(
    data: T.API.NativeLoyaltyGiftCardIssueRequest
  ): Promise<T.API.NativeLoyaltyGiftCardIssueResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-cards/issue',
      data,
    });
  }

  async native_loyalty_gift_card_recharge(
    data: T.API.NativeLoyaltyGiftCardIssueRequest
  ): Promise<T.API.NativeLoyaltyGiftCardIssueResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-cards/recharge',
      data,
    });
  }

  async native_loyalty_gift_card_purchase_get(
    data: T.API.NativeLoyaltyGiftCardPurchaseGetRequest
  ): Promise<T.API.NativeLoyaltyGiftCardPurchaseGetResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-card-purchases/get',
      data,
    });
  }

  async native_loyalty_gift_card_purchase_confirm(
    data: T.API.NativeLoyaltyGiftCardPurchaseReviewRequest
  ): Promise<T.API.NativeLoyaltyGiftCardPurchaseReviewResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-card-purchases/confirm',
      data,
    });
  }

  async native_loyalty_gift_card_purchase_reject(
    data: T.API.NativeLoyaltyGiftCardPurchaseReviewRequest
  ): Promise<T.API.NativeLoyaltyGiftCardPurchaseReviewResponse> {
    return this.request_auth({
      path: '/v1/native-loyalty/gift-card-purchases/reject',
      data,
    });
  }

  async stripe_connect_order_find(data: any): Promise<any> {
    return this.request_auth({
      path: '/dashboard/connect/transactions',
      data,
    });
  }

  async stripe_connect_payout_list(data: any): Promise<any> {
    return await this.request_auth({
      path: '/v2/stripe/payout/list',
      data,
    });
  }

  async stripe_connect_payout_details(data: any): Promise<any> {
    return await this.request_auth({
      path: '/v2/stripe/payout/get-transactions',
      data,
    });
  }

  async stripe_connect_payout_request_report(data: any): Promise<any> {
    return await this.request_auth({
      path: '/v2/stripe/payout/request-report',
      data,
    });
  }

  async getStripeTransaction(data: any): Promise<any> {
    return await this.request_auth({
      path: '/dashboard/connect/get-transaction',
      data,
    });
  }

  async create_lalamove_quotation(data: T.API.CreateLalamoveQuoteRequest): Promise<T.API.CreateLalamoveQuoteResponse> {
    return this.request_auth({
      path: '/dashboard/order/lalamove/delivery-quotes',
      data,
    });
  }

  async create_lalamove_delivery(
    data: T.API.DashboardCreateLalamoveDeliveryRequest
  ): Promise<T.API.DashboardCreateLalamoveDeliveryResponse> {
    return this.request_auth({
      path: '/dashboard/order/lalamove/deliveries',
      data,
    });
  }

  async findOrderByLalamoveDeliveryId(
    data: T.API.DashboardOrderFindByLalamoveOrderIdRequest
  ): Promise<T.API.DashboardOrderFindResponse> {
    return this.request_auth({
      path: '/dashboard/order/find-by-lalamove-order-id',
      data,
    });
  }

  async create_uber_quotation(data: T.API.CreateUberQuoteRequest): Promise<T.API.CreateUberQuoteResponse> {
    return this.request_auth({
      path: '/dashboard/order/uber/delivery-quotes',
      data,
    });
  }

  async create_uber_delivery(
    data: T.API.DashboardCreateUberDeliveryRequest
  ): Promise<T.API.DashboardCreateUberDeliveryResponse> {
    return this.request_auth({
      path: '/dashboard/order/uber/deliveries',
      data,
    });
  }

  async get_uber_delivery(data: T.API.GetUberDeliveryRequest): Promise<T.API.GetUberDeliveryResponse> {
    return this.request_auth({
      path: '/dashboard/order/uber/delivery',
      data,
    });
  }

  async get_serviceable_zones(data: T.API.GetUberServiceableRequest): Promise<T.API.GetUberServiceableResponse> {
    return this.request_auth({
      path: '/dashboard/uber/serviceable/list',
      data,
    });
  }

  async order_eta(data: T.API.DashboardOrderETAReq): Promise<T.API.StoresOrderETARes> {
    return this.request_auth({
      path: '/dashboard/order/eta',
      data,
    });
  }

  async order_dish_status_change(data: T.API.OrderDishStatusChangeReq): Promise<T.API.OrderDishStatusChangeRes> {
    return this.request_auth({
      path: '/dashboard/order_dish/update/status',
      data,
    });
  }

  async getWalletlyDeal(data: { _id: string }): Promise<T.API.GetDealsResponse> {
    return this.request_auth({
      path: '/v1/loyalty/walletly/get-deals',
      data,
    });
  }

  async updateDeal(data: any): Promise<T.API.UpdateDealsRequest> {
    return this.request_auth({
      path: '/v1/loyalty/walletly/update-deal',
      data,
    });
  }

  async stripeOnboarding(
    data: T.API.DashboardStripeOnboardingRequest
  ): Promise<T.API.DashboardStripeOnboardingResponse> {
    return this.request_auth({
      path: '/restaurants/payment/stripe/onboarding',
      data,
    });
  }

  async googleMapsAutocomplete(data: any) {
    return this.request_auth({
      path: '/v1/gmaps/dashboard/places/autocomplete',
      data,
    });
  }
  async googleMapsPlaceGet(data: any) {
    return this.request_auth({
      path: '/v1/gmaps/dashboard/places/details',
      data,
    });
  }

  async googleMapsGeolocate() {
    return this.request_auth({
      path: '/v1/gmaps/dashboard/geolocate',
    });
  }

  async googleMapsTimezoneFromCoords(data: any) {
    return this.request_auth({
      path: '/v1/gmaps/dashboard/timezone',
      data,
    });
  }

  async googleFonts() {
    return this.request_auth({
      path: '/v1/google/dashboard/fonts',
    });
  }

  async getMaintenance(): Promise<T.API.ConfigMaintenanceResponse> {
    return this.request_public({
      path: '/dashboard/maintenance',
    });
  }

  async receipt_settings_find(data: { restaurant_id: string }): Promise<{
    outcome: 0 | 1;
    receipt_mode?: T.Schema.Restaurant.RestaurantSettings['receipt_mode'];
    message?: string;
  }> {
    return this.request_auth({
      path: '/dashboard/restaurant/receipt-settings',
      data,
    });
  }

  async receipt_settings_update(data: {
    restaurant_id: string;
    receipt_mode: T.Schema.Restaurant.RestaurantSettings['receipt_mode'];
  }): Promise<{
    outcome: 0 | 1;
    receipt_mode?: T.Schema.Restaurant.RestaurantSettings['receipt_mode'];
    message?: string;
  }> {
    return this.request_auth({
      path: '/dashboard/restaurant/receipt-settings/update',
      data,
    });
  }

  async receipt_settings_preview(data: {
    restaurant_id: string;
    receipt_mode?: T.Schema.Restaurant.RestaurantSettings['receipt_mode'];
  }): Promise<{
    outcome: 0 | 1;
    html?: string;
    mode?: 'legacy' | 'modern';
    message?: string;
  }> {
    return this.request_auth({
      path: '/dashboard/restaurant/receipt-settings/preview',
      data,
    });
  }

  async receipt_settings_test_send(data: {
    restaurant_id: string;
    receipt_mode?: T.Schema.Restaurant.RestaurantSettings['receipt_mode'];
  }): Promise<{
    outcome: 0 | 1;
    email?: string;
    message?: string;
  }> {
    return this.request_auth({
      path: '/dashboard/restaurant/receipt-settings/test-send',
      data,
    });
  }

  // HELPERS
  private _get_auth_token(): string {
    return this.store.auth.token || '';
  }

  //Menu API

  async request_menu_api(args: T.API.RequestArgs): Promise<any> {
    const { path, data, params, headers, method = 'post' } = args;
    try {
      const res = await axios({
        baseURL: config.urls.menu_api.url + '/',
        url: path,
        method: method,
        headers: headers || {},
        params: params || {},
        data: data || {},
      });
      this.note_menu_version_from_headers(path, res.headers, method);
      return res.data;
    } catch (e) {
      if (e.response && e.response.data) {
        // === "error-auth-token"
        if (e.response.data.message === 'Invalid Authentication') {
          this.handlers.auth_token_error();
          return { outcome: 1, message: '' };
        } else {
          // Carry the status through. A 409 from menu-service means someone
          // else saved first, which callers render as a dialog rather than a
          // toast; the body alone cannot be told apart from any other failure.
          throw { ...e.response.data, status: e.response.status };
        }
      } else {
        throw e;
      }
    }
  }

  /**
   * Menu-service reports the lock in a header, since the same value has to
   * reach 37 endpoints with different response shapes.
   *
   * Only a write this session made counts. The header carries the document's
   * current version, which has also moved when someone else saved — recording
   * that would tell the session it had seen work it has not, and its next save
   * would overwrite rather than be refused. A write of our own is different:
   * we know what changed, because we changed it.
   */
  private note_menu_version_from_headers(path: string, headers: any, method?: string) {
    if (!method || method.toUpperCase() === 'GET') return;

    const value = Number(headers?.['x-menu-version'] ?? headers?.['X-Menu-Version']);
    if (!value) return;

    const restaurant_id = /^api\/([^/]+)\//.exec(path)?.[1];
    if (restaurant_id) this.note_menu_version(restaurant_id, value);
  }

  /**
   * Identity headers for menu version history.
   *
   * The API key says which integration is calling, not which person, so
   * menu-service cannot attribute a change without this. Values are
   * percent-encoded because HTTP headers must be ASCII and names are not
   * always; menu-service decodes them.
   */
  private menu_api_actor_headers(): T.ObjectAny {
    const user = this.store.auth.item;
    if (!user?._id) return {};

    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    const headers: T.ObjectAny = { 'X-Actor-Id': encodeURIComponent(user._id) };
    if (name) headers['X-Actor-Name'] = encodeURIComponent(name);
    if (user.email) headers['X-Actor-Email'] = encodeURIComponent(user.email);
    return headers;
  }

  async request_auth_menu_api(args: T.API.RequestArgs): Promise<any> {
    const { path, data, params, method, headers } = args;
    const token = config.urls.menu_api.api_key;
    if (config.isTest && !token) return;
    let headersOpts: T.ObjectAny | undefined = {
      Authorization: `${token}`,
      crossDomain: true,
      'X-Schema-Version': this.store.organisation?.menu || 'original',
      ...this.menu_api_actor_headers(),
    };
    if (headers) {
      headersOpts = {
        ...headersOpts,
        ...headers!,
      };
    }

    return this.request_menu_api({ path, data, params, headers: headersOpts, method });
  }
  //menus
  async menu_import(data: {
    restaurant_id: string;
    menu: T.Schema.Restaurant.Menu.RestaurantMenu | T.Schema.Restaurant.Menu.RestaurantMenuV2;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: `api/${data.restaurant_id}/menus/import`,
      method: 'POST',
      data: data.menu,
    });
  }

  // MENU VERSIONS

  async menu_versions_list(restaurant_id: string, limit?: number): Promise<T.API.MenuVersionListResponse> {
    const res: T.API.MenuVersionListResponse = await this.request_auth_menu_api({
      path: `api/${restaurant_id}/menus/versions${limit ? `?limit=${limit}` : ''}`,
      method: 'GET',
    });
    // Deliberately not noted as this session's lock. Reading the history is not
    // seeing someone else's save: recording it here would let a session with
    // the modal open save over work it never looked at. The restore passes the
    // value from its own list response instead.
    return res;
  }

  async menu_versions_diff(data: {
    restaurant_id: string;
    version_id: string;
    against?: string;
    menu_id?: string;
  }): Promise<T.API.MenuVersionDiffResponse> {
    const params: T.ObjectAny = {};
    if (data.against) params.against = data.against;
    if (data.menu_id) params.menu_id = data.menu_id;

    return this.request_auth_menu_api({
      path: `api/${data.restaurant_id}/menus/versions/${data.version_id}/diff`,
      method: 'GET',
      params,
    });
  }

  /**
   * expected_menu_version is the lock as this client last saw it: the restore
   * is refused with a 409 if someone changed the menu in the meantime, rather
   * than silently overwriting their work.
   */
  async menu_versions_restore(data: {
    restaurant_id: string;
    version_id: string;
    expected_menu_version?: number;
    menu_id?: string;
    reason?: string;
  }): Promise<T.API.MenuVersionRestoreResponse> {
    const expected = data.expected_menu_version ?? this.menu_version_for(data.restaurant_id);

    const res: T.API.MenuVersionRestoreResponse = await this.request_auth_menu_api({
      path: `api/${data.restaurant_id}/menus/versions/${data.version_id}/restore`,
      method: 'POST',
      data: {
        expected_menu_version: expected ?? 0,
        menu_id: data.menu_id ?? '',
        reason: data.reason ?? '',
      },
    });

    // A restore advances the lock. Without recording it, the operator's next
    // ordinary save would be refused by the restore they just performed.
    this.note_menu_version(data.restaurant_id, res?.menu_version);
    return res;
  }

  async menu_versions_copy_menu(data: {
    restaurant_id: string;
    version_id: string;
    menu_id: string;
    name?: string;
    reason?: string;
  }): Promise<T.API.MenuVersionCopyResponse> {
    const res: T.API.MenuVersionCopyResponse = await this.request_auth_menu_api({
      path: `api/${data.restaurant_id}/menus/versions/${data.version_id}/copy`,
      method: 'POST',
      data: {
        menu_id: data.menu_id,
        name: data.name ?? '',
        reason: data.reason ?? '',
      },
    });

    this.note_menu_version(data.restaurant_id, res?.menu_version);
    return res;
  }

  async menu_get_menus(restaurant_id: string): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + restaurant_id + '/menus',
      method: 'GET',
    });
  }

  async menu_delete_menu(data: { restaurant_id: string; menu_id: string }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id,
      method: 'DELETE',
    });
  }

  async menu_create_menu(data: {
    restaurant_id: string;
    menu: T.Schema.Restaurant.Menu.RestaurantMenu | T.Schema.Restaurant.Menu.RestaurantMenuV2;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus',
      method: 'POST',
      data: { ...data.menu, restaurant_id: data.restaurant_id },
    });
  }

  async menu_copy_node(data: {
    restaurant_id: string;
    menu_id: string;
    type: 'menu' | 'category' | 'item';
    category_id?: string;
    item_id?: string;
  }): Promise<any> {
    const { restaurant_id, ...payload } = data;
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/copy',
      method: 'POST',
      data: payload,
    });
  }

  async menu_partial_update_menu(data: { restaurant_id: string; menu_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id,
      method: 'PATCH',
      data: data.data,
    });
  }

  async menu_full_update_menu(data: { restaurant_id: string; menu_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id,
      method: 'PUT',
      data: data.data,
    });
  }

  /** body: { order: string[] } - array of menu ids */
  async menu_order(data: { restaurant_id: string; order: string[] }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/order',
      method: 'PATCH',
      data: { order: data.order },
    });
  }

  //categories
  async menu_create_category(data: { restaurant_id: string; menu_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories',
      method: 'POST',
      data: data.data,
    });
  }

  async menu_delete_category(data: { restaurant_id: string; menu_id: string; category_id: string }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories/' + data.category_id,
      method: 'DELETE',
    });
  }

  async menu_partial_update_category(data: {
    restaurant_id: string;
    menu_id: string;
    category_id: string;
    data: any;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories/' + data.category_id,
      method: 'PATCH',
      data: data.data,
    });
  }

  async menu_partial_update_batch_categories(data: { restaurant_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/categories',
      method: 'PATCH',
      data: data.data,
    });
  }

  async menu_full_update_category(data: { restaurant_id: string; menu_id: string; category_id: string }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories/' + data.category_id,
      method: 'PUT',
    });
  }

  /** body: { order: string[] } - array of category ids */
  async menu_categories_order(data: { restaurant_id: string; menu_id: string; order: string[] }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories/order',
      method: 'PATCH',
      data: { order: data.order },
    });
  }

  //items
  async menu_create_item(data: {
    restaurant_id: string;
    menu_id: string;
    category_id: string;
    data: any;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories/' + data.category_id + '/items',
      method: 'POST',
      data: data.data,
    });
  }

  async menu_delete_item(data: {
    restaurant_id: string;
    menu_id: string;
    category_id: string;
    item_id: string;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path:
        'api/' +
        data.restaurant_id +
        '/menus/' +
        data.menu_id +
        '/categories/' +
        data.category_id +
        '/items/' +
        data.item_id,
      method: 'DELETE',
    });
  }

  async menu_partial_update_item(data: {
    restaurant_id: string;
    menu_id: string;
    category_id: string;
    item_id: string;
    data: any;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path:
        'api/' +
        data.restaurant_id +
        '/menus/' +
        data.menu_id +
        '/categories/' +
        data.category_id +
        '/items/' +
        data.item_id,
      method: 'PATCH',
      data: data.data,
    });
  }

  async menu_partial_update_batch_items(data: { restaurant_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/items',
      method: 'PATCH',
      data: data.data,
    });
  }

  async menu_full_update_item(data: {
    restaurant_id: string;
    menu_id: string;
    category_id: string;
    item_id: string;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path:
        'api/' +
        data.restaurant_id +
        '/menus/' +
        data.menu_id +
        '/categories/' +
        data.category_id +
        '/items/' +
        data.item_id,
      method: 'PUT',
    });
  }

  /** body: { order: string[] } - array of item ids */
  async menu_items_order(data: {
    restaurant_id: string;
    menu_id: string;
    category_id: string;
    order: string[];
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/menus/' + data.menu_id + '/categories/' + data.category_id + '/items/order',
      method: 'PATCH',
      data: { order: data.order },
    });
  }

  //modifiers
  // async menu_get_modifiers(restaurant_id: string): Promise<any> {
  //   return this.request_auth_menu_api({
  //     path: 'api/'+restaurant_id+'/modifiers',
  //     method: 'GET',
  //   })
  // }

  async menu_delete_modifier(data: { restaurant_id: string; modifier_id: string }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id,
      method: 'DELETE',
    });
  }

  async menu_create_modifier(data: { restaurant_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers',
      method: 'POST',
      data: data.data,
    });
  }

  async menu_partial_update_modifier(data: { restaurant_id: string; modifier_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id,
      method: 'PATCH',
      data: data.data,
    });
  }

  /** body: { order: string[] } - array of modifier ids */
  async modifiers_order(data: { restaurant_id: string; order: string[] }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/order',
      method: 'PATCH',
      data: { order: data.order },
    });
  }

  async menu_full_update_modifier(data: { restaurant_id: string; modifier_id: string }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id,
      method: 'PUT',
    });
  }

  //modifier options
  // async menu_get_modifiers(restaurant_id: string): Promise<any> {
  //   return this.request_auth_menu_api({
  //     path: 'api/'+restaurant_id+'/modifiers',
  //     method: 'GET',
  //   })
  // }

  async menu_delete_modifier_option(data: {
    restaurant_id: string;
    modifier_id: string;
    option_id: string;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id + '/options/' + data.option_id,
      method: 'DELETE',
    });
  }

  async menu_create_modifier_option(data: { restaurant_id: string; modifier_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id + '/options',
      method: 'POST',
      data: data.data,
    });
  }

  async menu_batch_upsert_modifier_options(data: {
    restaurant_id: string;
    modifier_id: string;
    options: any[];
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id + '/options/batch',
      method: 'POST',
      data: { options: data.options },
    });
  }

  async menu_partial_update_modifier_option(data: {
    restaurant_id: string;
    modifier_id: string;
    option_id: string;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id + '/options/' + data.option_id,
      method: 'PATCH',
    });
  }

  async menu_full_update_modifier_option(data: {
    restaurant_id: string;
    modifier_id: string;
    option_id: string;
  }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/modifiers/' + data.modifier_id + '/options/' + data.option_id,
      method: 'PUT',
    });
  }

  // tags (v2.0)
  async tags_list(restaurant_id: string): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + restaurant_id + '/tags',
      method: 'GET',
    });
  }

  async tags_create(data: { restaurant_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/tags',
      method: 'POST',
      data: data.data,
    });
  }

  async tags_update(data: { restaurant_id: string; tag_id: string; data: any }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/tags/' + data.tag_id,
      method: 'PATCH',
      data: data.data,
    });
  }

  async tags_delete(data: { restaurant_id: string; tag_id: string }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/tags/' + data.tag_id,
      method: 'DELETE',
    });
  }

  /** body: { order: string[] } - array of tag ids */
  async tags_order(data: { restaurant_id: string; order: string[] }): Promise<any> {
    return this.request_auth_menu_api({
      path: 'api/' + data.restaurant_id + '/tags/order',
      method: 'PATCH',
      data: { order: data.order },
    });
  }
}
